import { useState } from 'react';
import { toast } from 'sonner';
import { Shield, KeyRound, Smartphone, CheckCircle2, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { usePortalMe, useUpdatePassword, useResetMfaSelf } from '@/api/hooks/usePortal';

// ── Change password ────────────────────────────────────────────────────────────

function ChangePasswordSection() {
  const updatePassword = useUpdatePassword();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (next.length < 12) { setError('New password must be at least 12 characters'); return; }
    if (next !== confirm) { setError('Passwords do not match'); return; }
    try {
      await updatePassword.mutateAsync({ current_password: current, new_password: next });
      toast.success('Password changed successfully');
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { errors?: { message?: string } } } })
        ?.response?.data?.errors?.message;
      setError(msg ?? 'Failed to change password');
    }
  }

  return (
    <div className="rounded-lg border border-border-subtle bg-bg-surface p-5 space-y-4">
      <div className="flex items-center gap-2">
        <KeyRound className="h-4 w-4 text-text-secondary" />
        <h2 className="font-semibold text-text-primary">Change password</h2>
      </div>
      <form onSubmit={handleSubmit} className="space-y-3 max-w-sm">
        <div className="space-y-1.5">
          <Label htmlFor="current_pw">Current password</Label>
          <Input id="current_pw" type="password" autoComplete="current-password"
            value={current} onChange={(e) => setCurrent(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="new_pw">New password</Label>
          <Input id="new_pw" type="password" autoComplete="new-password" minLength={12}
            value={next} onChange={(e) => setNext(e.target.value)} />
          <p className="text-xs text-text-secondary">Must be at least 12 characters.</p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="confirm_pw">Confirm new password</Label>
          <Input id="confirm_pw" type="password" autoComplete="new-password"
            value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </div>
        {error && <p className="text-sm text-accent-rose">{error}</p>}
        <Button type="submit" disabled={updatePassword.isPending || !current || !next || !confirm}>
          {updatePassword.isPending ? 'Updating…' : 'Update password'}
        </Button>
      </form>
    </div>
  );
}

// ── MFA ────────────────────────────────────────────────────────────────────────

function MfaSection() {
  const { data: me, isLoading } = usePortalMe();
  const resetMfaSelf = useResetMfaSelf();
  const navigate = useNavigate();

  const [showReset, setShowReset] = useState(false);
  const [resetPw, setResetPw] = useState('');
  const [resetError, setResetError] = useState('');

  async function handleReset(e: React.FormEvent) {
    e.preventDefault();
    setResetError('');
    try {
      const result = await resetMfaSelf.mutateAsync({ password: resetPw });
      // VMFA-1 / ED-VMFA-1-ENROLLMENT-TARGET-1: route to the WORKING re-enrollment page.
      // PortalMfaEnrollmentPage reads setupToken from location.state and sends it in the
      // request body. /portal/mfa/setup (PortalMfaSetupPage) is dead — it calls
      // /portal/auth/mfa/{setup,verify}, which do not exist. No auth store involved.
      navigate('/portal/mfa/enrollment', { state: { setupToken: result.setupToken } });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { errors?: { message?: string } } } })
        ?.response?.data?.errors?.message;
      setResetError(msg ?? 'Failed to reset MFA');
    }
  }

  if (isLoading) return <Skeleton className="h-32 w-full rounded-lg" />;

  return (
    <div className="rounded-lg border border-border-subtle bg-bg-surface p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Smartphone className="h-4 w-4 text-text-secondary" />
          <h2 className="font-semibold text-text-primary">Two-factor authentication</h2>
        </div>
        {me?.mfa_enabled
          ? <div className="flex items-center gap-1.5 text-sm text-accent-emerald">
              <CheckCircle2 className="h-4 w-4" />Enabled
            </div>
          : <div className="flex items-center gap-1.5 text-sm text-accent-amber">
              <AlertTriangle className="h-4 w-4" />Not enabled
            </div>
        }
      </div>

      {me?.mfa_enabled ? (
        <div className="space-y-3">
          <p className="text-sm text-text-secondary">
            Your account is protected with an authenticator app.
            {me.mfa_enrolled_at && (
              <> Enrolled {new Date(me.mfa_enrolled_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}.</>
            )}
          </p>

          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" size="sm" onClick={() => { setShowReset(!showReset); }}>
              Re-enroll authenticator
            </Button>
          </div>

          {showReset && (
            <form onSubmit={handleReset} className="space-y-3 pt-2 border-t border-border-subtle max-w-sm">
              <p className="text-xs text-text-secondary">Enter your password to clear your current authenticator and start re-enrollment.</p>
              <div className="space-y-1.5">
                <Label htmlFor="rst_pw">Password</Label>
                <Input id="rst_pw" type="password" value={resetPw} onChange={(e) => setResetPw(e.target.value)} />
              </div>
              {resetError && <p className="text-sm text-accent-rose">{resetError}</p>}
              <div className="flex gap-2">
                <Button type="submit" variant="outline" size="sm"
                  disabled={resetMfaSelf.isPending || !resetPw}>
                  {resetMfaSelf.isPending ? 'Processing…' : 'Re-enroll authenticator'}
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => setShowReset(false)}>Cancel</Button>
              </div>
            </form>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-text-secondary">
            Add an extra layer of security to your account with an authenticator app.
          </p>
          <Button size="sm" onClick={() => navigate('/portal/mfa/setup')}>
            Set up 2FA
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

export function PortalSecurityPage() {
  return (
    <div className="space-y-6 max-w-lg">
      <div className="flex items-center gap-2">
        <Shield className="h-5 w-5 text-text-secondary" />
        <h1 className="text-lg font-bold text-text-primary">Security</h1>
      </div>
      <ChangePasswordSection />
      <MfaSection />
    </div>
  );
}
